import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Plus, Trash2, AlertTriangle, ArrowRight } from 'lucide-react';
import {
  useDependenciesQuery,
  useCreateDependency,
  useUpdateDependency,
  useDeleteDependency,
  type DependencyType,
} from '@/hooks/useDependencies';
import { useTasksQuery } from '@/hooks/useTasks';

interface Task {
  id: string;
  sheetId: string;
  rowNumber: number;
  name: string;
}

interface TaskDependenciesProps {
  task: Task;
  sheetId: string;
}

const DEPENDENCY_TYPES: Array<{ value: DependencyType; label: string; description: string }> = [
  { value: 'FS', label: 'Finish-to-Start', description: 'Predecessor must finish before this task starts' },
  { value: 'SS', label: 'Start-to-Start', description: 'Predecessor must start before this task starts' },
  { value: 'FF', label: 'Finish-to-Finish', description: 'Predecessor must finish before this task finishes' },
  { value: 'SF', label: 'Start-to-Finish', description: 'Predecessor must start before this task finishes' },
];

const DEPENDENCY_TYPE_COLORS: Record<DependencyType, string> = {
  FS: 'bg-blue-100 text-blue-800',
  SS: 'bg-green-100 text-green-800',
  FF: 'bg-purple-100 text-purple-800',
  SF: 'bg-orange-100 text-orange-800',
};

export function TaskDependencies({ task, sheetId }: TaskDependenciesProps) {
  const { data: dependencies, isLoading } = useDependenciesQuery(task.id);
  const { data: allTasks } = useTasksQuery(sheetId);
  const createDependency = useCreateDependency(sheetId);
  const updateDependency = useUpdateDependency(sheetId);
  const deleteDependency = useDeleteDependency(sheetId);

  const [isAddingPredecessor, setIsAddingPredecessor] = useState(false);
  const [selectedPredecessorId, setSelectedPredecessorId] = useState<string>('');
  const [selectedType, setSelectedType] = useState<DependencyType>('FS');
  const [lag, setLag] = useState('0');
  const [cycleWarning, setCycleWarning] = useState<string | null>(null);

  // Filter out tasks that are already predecessors or the task itself
  const availablePredecessors = allTasks?.filter(
    (t) =>
      t.id !== task.id &&
      !dependencies?.predecessors.some((d) => d.predecessorId === t.id)
  );

  const handleAddPredecessor = useCallback(async () => {
    if (!selectedPredecessorId) return;

    setCycleWarning(null);

    try {
      await createDependency.mutateAsync({
        predecessorId: selectedPredecessorId,
        successorId: task.id,
        type: selectedType,
        lag: parseFloat(lag) || 0,
        lagUnit: 'd',
      });

      setIsAddingPredecessor(false);
      setSelectedPredecessorId('');
      setSelectedType('FS');
      setLag('0');
    } catch (error) {
      // Check if it's a cycle error
      const errorMessage = (error as Error).message || '';
      if (errorMessage.toLowerCase().includes('cycle')) {
        setCycleWarning('Adding this dependency would create a circular reference.');
      }
    }
  }, [selectedPredecessorId, task.id, selectedType, lag, createDependency]);

  const handleUpdateType = useCallback(
    (dependencyId: string, newType: DependencyType) => {
      updateDependency.mutate({
        dependencyId,
        updates: { type: newType },
      });
    },
    [updateDependency]
  );

  const handleUpdateLag = useCallback(
    (dependencyId: string, newLag: string) => {
      const lagValue = parseFloat(newLag);
      if (!isNaN(lagValue)) {
        updateDependency.mutate({
          dependencyId,
          updates: { lag: lagValue },
        });
      }
    },
    [updateDependency]
  );

  const handleDeleteDependency = useCallback(
    (dependencyId: string) => {
      deleteDependency.mutate(dependencyId);
    },
    [deleteDependency]
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-32 bg-gray-200 animate-pulse rounded" />
        <div className="h-20 bg-gray-100 animate-pulse rounded" />
      </div>
    );
  }

  const predecessors = dependencies?.predecessors || [];
  const successors = dependencies?.successors || [];

  return (
    <div className="space-y-6">
      {/* Predecessors Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Predecessors
          </h3>
          <Popover open={isAddingPredecessor} onOpenChange={setIsAddingPredecessor}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <h4 className="font-medium">Add Predecessor</h4>

                {cycleWarning && (
                  <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {cycleWarning}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Task</Label>
                  <Select
                    value={selectedPredecessorId}
                    onValueChange={setSelectedPredecessorId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select task..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePredecessors?.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          #{t.rowNumber} - {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={selectedType}
                    onValueChange={(v) => setSelectedType(v as DependencyType)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPENDENCY_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex flex-col">
                            <span>{type.label} ({type.value})</span>
                            <span className="text-xs text-muted-foreground">
                              {type.description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Lag (days)</Label>
                  <Input
                    type="number"
                    value={lag}
                    onChange={(e) => setLag(e.target.value)}
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use negative values for lead time
                  </p>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsAddingPredecessor(false);
                      setCycleWarning(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAddPredecessor}
                    disabled={!selectedPredecessorId || createDependency.isPending}
                  >
                    {createDependency.isPending ? 'Adding...' : 'Add'}
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {predecessors.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center border rounded-lg border-dashed">
            No predecessors. This task can start immediately.
          </p>
        ) : (
          <div className="space-y-2">
            {predecessors.map((dep) => (
              <DependencyItem
                key={dep.id}
                dependency={dep}
                taskName={dep.predecessorTask?.name || 'Unknown Task'}
                taskRowNumber={dep.predecessorTask?.rowNumber || 0}
                onUpdateType={(type) => handleUpdateType(dep.id, type)}
                onUpdateLag={(newLag) => handleUpdateLag(dep.id, newLag)}
                onDelete={() => handleDeleteDependency(dep.id)}
                canEdit={true}
              />
            ))}
          </div>
        )}
      </div>

      {/* Successors Section */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Successors
        </h3>

        {successors.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center border rounded-lg border-dashed">
            No successors. No tasks depend on this one.
          </p>
        ) : (
          <div className="space-y-2">
            {successors.map((dep) => (
              <DependencyItem
                key={dep.id}
                dependency={dep}
                taskName={dep.successorTask?.name || 'Unknown Task'}
                taskRowNumber={dep.successorTask?.rowNumber || 0}
                onUpdateType={(type) => handleUpdateType(dep.id, type)}
                onUpdateLag={(newLag) => handleUpdateLag(dep.id, newLag)}
                onDelete={() => handleDeleteDependency(dep.id)}
                canEdit={false}
                isSuccessor={true}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface DependencyItemProps {
  dependency: {
    id: string;
    type: DependencyType;
    lag: number;
    lagUnit: string;
  };
  taskName: string;
  taskRowNumber: number;
  onUpdateType: (type: DependencyType) => void;
  onUpdateLag: (lag: string) => void;
  onDelete: () => void;
  canEdit: boolean;
  isSuccessor?: boolean;
}

function DependencyItem({
  dependency,
  taskName,
  taskRowNumber,
  onUpdateType,
  onUpdateLag,
  onDelete,
  canEdit,
  isSuccessor = false,
}: DependencyItemProps) {
  const [editingLag, setEditingLag] = useState(false);
  const [lagValue, setLagValue] = useState(dependency.lag.toString());

  const handleLagBlur = () => {
    setEditingLag(false);
    if (lagValue !== dependency.lag.toString()) {
      onUpdateLag(lagValue);
    }
  };

  const formatLag = (lag: number) => {
    if (lag === 0) return null;
    if (lag > 0) return `+${lag}d`;
    return `${lag}d`;
  };

  return (
    <div className="flex items-center gap-2 p-3 border rounded-lg bg-gray-50">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            #{taskRowNumber}
          </span>
          {isSuccessor && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
          <span className="text-sm font-medium truncate">{taskName}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {canEdit ? (
          <Select
            value={dependency.type}
            onValueChange={(v) => onUpdateType(v as DependencyType)}
          >
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DEPENDENCY_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge className={DEPENDENCY_TYPE_COLORS[dependency.type]}>
            {dependency.type}
          </Badge>
        )}

        {canEdit && editingLag ? (
          <Input
            type="number"
            value={lagValue}
            onChange={(e) => setLagValue(e.target.value)}
            onBlur={handleLagBlur}
            onKeyDown={(e) => e.key === 'Enter' && handleLagBlur()}
            className="w-16 h-8"
            autoFocus
          />
        ) : (
          <button
            onClick={() => canEdit && setEditingLag(true)}
            className={`text-sm ${canEdit ? 'cursor-pointer hover:bg-gray-200 px-2 py-1 rounded' : ''}`}
            disabled={!canEdit}
          >
            {formatLag(dependency.lag) || (canEdit ? '±0d' : '—')}
          </button>
        )}

        {canEdit && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove Dependency</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to remove this dependency? This may affect
                  task scheduling.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

export default TaskDependencies;
